import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, ArrowLeft, LifeBuoy, Mail, MessageCircle, Ticket } from "lucide-react";
import { areaLabelForPath } from "../lib/accessRestrictedExplainer";
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_WHATSAPP_URL,
  SUPPORT_TICKET_PATH,
} from "../lib/supportContact";

/**
 * Sprint RBAC-X · Slice 1 — Access Restricted page (security-hardened).
 *
 * Iter 73 (Feb 2026) — security hardening: the previous variant
 * surfaced the caller's role + the role(s) required for the route.
 * That was flagged as an info-disclosure surface because it lets an
 * attacker map out internal role names and target social-engineering
 * attacks at the higher-tier roles.  The page now reveals NOTHING
 * about the role system or the route internals.  Instead it directs
 * the user to the support team with email / WhatsApp / ticket CTAs.
 *
 * Props are intentionally ignored on the render path — kept in the
 * signature only so existing call sites (<ProtectedRoute roles={…}
 * userRole={…}>) continue to work without churn.
 */
// eslint-disable-next-line no-unused-vars
export default function AccessRestrictedPage({ allowedRoles, userRole }) {
  const loc = useLocation();
  const attemptedPath = loc.state?.attemptedPath || loc.pathname;
  const area = areaLabelForPath(attemptedPath);

  return (
    <div
      data-testid="access-restricted-page"
      className="min-h-screen bg-[#0A192F] text-white relative overflow-hidden flex items-center justify-center px-6 py-16"
    >
      {/* Soft gold halo */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[42rem] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 35%, transparent 70%)",
          filter: "blur(48px)",
        }}
      />

      <div
        className="relative z-10 max-w-2xl w-full text-center"
        data-testid="access-restricted-card"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[rgba(212,175,55,0.12)] border border-[rgba(212,175,55,0.4)] mb-8">
          <ShieldAlert
            className="w-9 h-9 text-[#D4AF37]"
            data-testid="access-restricted-icon"
            aria-hidden="true"
          />
        </div>

        <p className="text-[11px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">
          Diplomexa · Security
        </p>

        <h1
          data-testid="access-restricted-title"
          className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-2"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          Access <span className="italic text-[#D4AF37]">restricted</span>
        </h1>

        <div className="w-16 h-px bg-[#D4AF37] mx-auto my-8" />

        <p
          data-testid="access-restricted-message"
          className="text-base sm:text-lg text-slate-300 leading-relaxed mb-2"
        >
          You do not have permission to view this page. Please contact your
          event organiser or Diplomexa support if you believe this is a mistake.
        </p>

        {/* ── Support card — replaces the old role explainer ─────────── */}
        <div
          data-testid="access-restricted-support-card"
          className="mt-8 mx-auto max-w-xl text-left rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur px-5 py-5"
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-4">
            <LifeBuoy className="w-3.5 h-3.5" aria-hidden="true" />
            Need help? Contact support
          </div>

          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.25)] grid place-items-center">
                <Mail className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Email</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="access-restricted-support-email"
                  className="text-sm text-slate-100 hover:text-[#D4AF37] transition-colors break-all"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </li>

            <li className="flex items-center gap-3">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.25)] grid place-items-center">
                <MessageCircle className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Mobile / WhatsApp</p>
                <a
                  href={SUPPORT_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="access-restricted-support-phone"
                  className="text-sm text-slate-100 hover:text-[#D4AF37] transition-colors"
                >
                  {SUPPORT_PHONE_DISPLAY}
                </a>
              </div>
            </li>

            <li className="flex items-center gap-3">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.25)] grid place-items-center">
                <Ticket className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Support ticket</p>
                <Link
                  to={SUPPORT_TICKET_PATH}
                  data-testid="access-restricted-support-ticket"
                  className="text-sm text-slate-100 hover:text-[#D4AF37] transition-colors"
                >
                  Raise a support ticket
                </Link>
              </div>
            </li>
          </ul>

          <div className="mt-4 pt-3 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
              You were trying to access
            </p>
            <span
              data-testid="access-restricted-area-chip"
              className="inline-block mt-1 px-3 py-1 text-xs rounded-full bg-white/5 border border-white/15 text-slate-200"
            >
              {area}
            </span>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/dashboard"
            data-testid="access-restricted-back-button"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D4AF37] text-[#0A192F] font-semibold hover:bg-[#E5C158] transition-all hover:-translate-y-0.5 shadow-lg shadow-[rgba(212,175,55,0.25)]"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Go back to dashboard
          </Link>
          <Link
            to={SUPPORT_TICKET_PATH}
            data-testid="access-restricted-support-link"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <LifeBuoy className="w-4 h-4" aria-hidden="true" />
            Contact support
          </Link>
        </div>
      </div>
    </div>
  );
}
